import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { HealthRing } from "../components/HealthRing";
import { StatusBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { fcfa, money, shortDate, PRODUCTS } from "../lib/format";
import { Building2, Plus, Wrench, Users, HeartPulse, Wallet, ArrowRight, MapPin, Loader2 } from "lucide-react";

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <Card className="p-5 border-border hover:shadow-lg hover:-translate-y-0.5 transition-[transform,box-shadow]" data-testid={`stat-${label.toLowerCase().replace(/ /g, "-")}`}>
      <div className="flex items-center justify-between">
        <p className="overline text-muted-foreground">{label}</p>
        <Icon className={`h-4 w-4 ${accent || "text-primary"}`} aria-hidden="true" />
      </div>
      <p className="font-heading text-2xl md:text-3xl font-bold tracking-tight mt-3 break-words">{value}</p>
    </Card>
  );
}

function MoneyStat({ icon: Icon, label, byCur, accent }) {
  const entries = Object.entries(byCur || {}).filter(([, v]) => v > 0);
  return (
    <Card className="p-5 border-border hover:shadow-lg hover:-translate-y-0.5 transition-[transform,box-shadow]" data-testid={`stat-${label.toLowerCase().replace(/ /g, "-")}`}>
      <div className="flex items-center justify-between">
        <p className="overline text-muted-foreground">{label}</p>
        <Icon className={`h-4 w-4 ${accent || "text-primary"}`} aria-hidden="true" />
      </div>
      {entries.length === 0 ? (
        <p className="font-heading text-2xl md:text-3xl font-bold tracking-tight mt-3">{money(0)}</p>
      ) : (
        <div className="mt-3 space-y-0.5">
          {entries.map(([c, v]) => (
            <p key={c} className="font-heading text-lg font-bold tracking-tight break-words">{money(v, c)}</p>
          ))}
        </div>
      )}
    </Card>
  );
}

function PropertyCard({ p }) {
  const prod = PRODUCTS[p.product] || PRODUCTS.CARE;
  return (
    <Link to={`/properties/${p.id}`} data-testid={`property-card-${p.code}`}>
      <Card className="p-6 border-border hover:shadow-lg hover:-translate-y-1 transition-[transform,box-shadow] h-full">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <span className={`inline-block text-[0.6rem] uppercase tracking-widest text-white px-2 py-0.5 rounded-full ${prod.color}`}>
              {prod.label}
            </span>
            <h3 className="font-heading text-xl font-bold tracking-tight mt-3 truncate">{p.property_name}</h3>
            <p className="text-sm text-muted-foreground font-mono mt-0.5">{p.code}</p>
            <p className="flex items-center gap-1 text-sm text-muted-foreground mt-2">
              <MapPin className="h-3.5 w-3.5" aria-hidden="true" /> {p.address}
            </p>
          </div>
          <HealthRing score={p.health_score} size={88} stroke={9} />
        </div>
        {p.priorities?.length > 0 && (
          <div className="mt-4 pt-4 border-t border-border">
            <p className="overline text-muted-foreground mb-2">Top priority</p>
            <div className="flex items-center gap-2 text-sm">
              <span className="h-2 w-2 rounded-full bg-accent" />
              {p.priorities[0].label}
              <span className="text-muted-foreground">· {p.priorities[0].score}/100</span>
            </div>
          </div>
        )}
        <div className="mt-4 flex items-center text-sm text-primary font-medium">
          View property <ArrowRight className="h-4 w-4 ml-1" aria-hidden="true" />
        </div>
      </Card>
    </Link>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const isAdmin = ["admin", "manager"].includes(user.role);
  const [properties, setProperties] = useState([]);
  const [stats, setStats] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const calls = [api.get("/properties"), api.get("/maintenance-requests")];
    if (isAdmin) calls.push(api.get("/admin/stats"));
    Promise.all(calls).then((res) => {
      setProperties(res[0].data);
      setRequests(res[1].data);
      if (isAdmin) setStats(res[2].data);
    }).finally(() => setLoading(false));
  }, [isAdmin]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="fade-up">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="overline text-accent">{isAdmin ? "Operations" : "My properties"}</p>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">
            {isAdmin ? "Admin Dashboard" : `Hello, ${user.name.split(" ")[0]}`}
          </h1>
          {!isAdmin && <p className="text-muted-foreground mt-1">Your property, monitored and maintained — wherever you are.</p>}
        </div>
        {!isAdmin && (
          <Link to="/properties/new">
            <Button className="rounded-full hover:bg-primary/90" data-testid="register-property-cta">
              <Plus className="h-4 w-4 mr-2" aria-hidden="true" /> Register Property
            </Button>
          </Link>
        )}
      </div>

      {isAdmin && stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <Stat icon={Building2} label="Properties" value={stats.total_properties} />
          <Stat icon={Users} label="Customers" value={stats.total_customers} />
          <Stat icon={Wrench} label="Open Requests" value={stats.open_requests} accent="text-accent" />
          <Stat icon={HeartPulse} label="Avg Health" value={`${stats.avg_health}/100`} />
          <Stat icon={Wrench} label="In Progress" value={stats.in_progress_requests} accent="text-blue-600" />
          <Stat icon={Wrench} label="Completed" value={stats.completed_requests} accent="text-emerald-600" />
          <MoneyStat icon={Wallet} label="Outstanding" byCur={stats.outstanding_by_currency} accent="text-red-600" />
          <MoneyStat icon={Wallet} label="Collected" byCur={stats.collected_by_currency} accent="text-emerald-600" />
        </div>
      )}

      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-heading text-xl font-bold tracking-tight">
          {isAdmin ? "All Properties" : "Property Portfolio"}
        </h2>
      </div>

      {properties.length === 0 ? (
        <Card className="p-12 text-center border-dashed">
          <Building2 className="h-10 w-10 mx-auto text-muted-foreground mb-4" aria-hidden="true" />
          <p className="font-heading text-lg font-bold">No properties yet</p>
          <p className="text-muted-foreground text-sm mt-1 mb-4">Register your first property to request inspections and maintenance.</p>
          {!isAdmin && <Link to="/properties/new"><Button className="rounded-full">Register Property</Button></Link>}
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {properties.map((p) => <PropertyCard key={p.id} p={p} />)}
        </div>
      )}

      {requests.length > 0 && (
        <div className="mt-12">
          <h2 className="font-heading text-xl font-bold tracking-tight mb-4">Recent Requests</h2>
          <Card className="border-border divide-y divide-border">
            {requests.slice(0, 5).map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-4 p-4">
                <div className="min-w-0">
                  <p className="font-medium truncate">{r.category}</p>
                  <p className="text-sm text-muted-foreground truncate">{r.description}</p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-xs text-muted-foreground hidden sm:block">{shortDate(r.created_at)}</span>
                  <StatusBadge status={r.status} />
                </div>
              </div>
            ))}
          </Card>
        </div>
      )}
    </div>
  );
}
